import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { FaInstagram, FaFacebook, FaTiktok } from "react-icons/fa6";
import { CONTACT_EMAILS, SOCIAL_LINKS } from "../lib/contact";
import { fadeUp, viewportOnce } from "../lib/motion";

const ENQUIRY_OPTIONS = [
    "General enquiry",
    "Join the pilot",
    "Professional application",
    "Partnership",
    "Media / press",
    "Other",
];

const SOCIAL_ICONS = {
    Instagram: FaInstagram,
    Facebook: FaFacebook,
    TikTok: FaTiktok,
};

export const SocialRow = ({ prefix }) => (
    <div className="flex items-center gap-3">
        {SOCIAL_LINKS.map((s) => {
            const Icon = SOCIAL_ICONS[s.label];
            return (
                <a
                    key={s.label}
                    href={s.url}
                    target={s.url === "#" ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    aria-label={`Follow BuildEase on ${s.label}`}
                    title={
                        s.url === "#"
                            ? `${s.label} — link to be configured`
                            : `Follow BuildEase on ${s.label}`
                    }
                    data-testid={`${prefix}-social-${s.label.toLowerCase()}`}
                    onClick={(e) => {
                        if (s.url === "#") e.preventDefault();
                    }}
                    className="flex h-10 w-10 items-center justify-center border border-white/15 text-mist transition-colors duration-300 hover:border-accent/60 hover:text-accent"
                >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
            );
        })}
    </div>
);

const ContactSection = () => {
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        subject: "",
        enquiryType: "",
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [reference, setReference] = useState("");

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/contact-messages`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        full_name: form.fullName.trim(),
                        email: form.email.trim(),
                        subject: form.subject.trim(),
                        enquiry_type: form.enquiryType || "General enquiry",
                        message: form.message.trim(),
                    }),
                },
            );
            if (!res.ok) throw new Error("failed");
            const data = await res.json();
            setReference(data.id);
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    const reset = () => {
        setForm({
            fullName: "",
            email: "",
            subject: "",
            enquiryType: "",
            message: "",
        });
        setStatus("idle");
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative overflow-hidden border-t border-white/10 bg-ink"
        >
            <div className="blueprint-bg blueprint-fade pointer-events-none absolute inset-0 opacity-50" />

            <div className="relative mx-auto max-w-[1600px] px-6 py-24 md:px-10 lg:px-16 lg:py-32">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="flex items-center gap-6"
                >
                    <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                        <span className="h-1.5 w-1.5 bg-accent" />
                        CONTACT
                    </p>
                    <span className="h-px flex-1 bg-white/10" />
                    <p className="font-mono text-[13px] tracking-[0.3em] text-cad">
                        C.01 — C.03
                    </p>
                </motion.div>

                <div className="mt-14 grid gap-14 lg:grid-cols-12">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="lg:col-span-5"
                    >
                        <h2
                            data-testid="contact-headline"
                            className="font-display text-4xl font-semibold leading-[1.12] text-white md:text-5xl"
                        >
                            Let's Build With Clarity
                            <span className="text-accent">.</span>
                        </h2>
                        <p className="mt-6 max-w-md text-[16px] leading-relaxed text-mist">
                            Have a question about BuildEase, the pilot, or your
                            construction project? Get in touch with our team.
                        </p>
                        <p className="mt-4 max-w-md text-[15px] leading-relaxed text-mist/80">
                            Whether you're planning your first home or looking
                            to work with BuildEase as a professional, we're
                            here to help.
                        </p>

                        <div className="mt-12">
                            {CONTACT_EMAILS.map((c, i) => (
                                <div
                                    key={c.label}
                                    data-testid={`contact-option-${i + 1}`}
                                    className="border-t border-white/10 py-5 last:border-b"
                                >
                                    <p className="font-mono text-[13px] tracking-[0.22em] text-accent">
                                        {c.label}
                                    </p>
                                    <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-mist">
                                        {c.desc}
                                    </p>
                                    <a
                                        href={`mailto:${c.email}${c.subject ? `?subject=${encodeURIComponent(c.subject)}` : ""}`}
                                        data-testid={`contact-email-${i + 1}`}
                                        className="mt-2.5 inline-block text-[15px] text-white transition-colors duration-300 hover:text-accent"
                                    >
                                        {c.email}
                                    </a>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                            <p className="font-mono text-[13px] tracking-[0.22em] text-mist">
                                FOLLOW BUILDEASE
                            </p>
                            <SocialRow prefix="contact" />
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="lg:col-span-7"
                    >
                        {status === "success" ? (
                            <div
                                data-testid="contact-success"
                                className="flex h-full flex-col items-start justify-center border border-accent/50 bg-[#0C1428] p-10 md:p-14"
                            >
                                <p className="font-mono text-[13px] tracking-[0.22em] text-accent">
                                    STATUS / RECEIVED
                                </p>
                                <h3 className="mt-6 font-display text-3xl font-semibold text-white">
                                    Message received
                                    <span className="text-accent">.</span>
                                </h3>
                                <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-mist">
                                    Thank you for contacting BuildEase. We'll
                                    be in touch.
                                </p>
                                <p className="mt-6 font-mono text-[13px] tracking-[0.18em] text-cad">
                                    REFERENCE — {reference}
                                </p>
                                <button
                                    type="button"
                                    onClick={reset}
                                    data-testid="contact-send-another"
                                    className="mt-8 border border-white/25 px-6 py-3 text-sm text-white transition-colors duration-300 hover:border-accent hover:text-accent"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid="contact-form"
                                onSubmit={submit}
                                className="border border-white/15 bg-[#0C1428] p-8 md:p-10"
                            >
                                <p className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                    DIRECT MESSAGE
                                </p>

                                <div className="mt-8 grid gap-8 sm:grid-cols-2">
                                    <div>
                                        <label
                                            htmlFor="contact-name"
                                            className="font-mono text-[13px] tracking-[0.22em] text-cad"
                                        >
                                            FULL NAME
                                        </label>
                                        <input
                                            id="contact-name"
                                            type="text"
                                            required
                                            data-testid="contact-input-name"
                                            value={form.fullName}
                                            onChange={set("fullName")}
                                            className="mt-2 w-full border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 hover:border-white/40 focus:border-accent"
                                            placeholder="—"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="contact-email"
                                            className="font-mono text-[13px] tracking-[0.22em] text-cad"
                                        >
                                            EMAIL ADDRESS
                                        </label>
                                        <input
                                            id="contact-email"
                                            type="email"
                                            required
                                            data-testid="contact-input-email"
                                            value={form.email}
                                            onChange={set("email")}
                                            className="mt-2 w-full border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 hover:border-white/40 focus:border-accent"
                                            placeholder="—"
                                        />
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <label
                                        htmlFor="contact-type"
                                        className="font-mono text-[13px] tracking-[0.22em] text-cad"
                                    >
                                        WHAT CAN WE HELP YOU WITH?
                                        <span className="ml-2 text-cad/70">
                                            OPTIONAL
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="contact-type"
                                            data-testid="contact-select-type"
                                            value={form.enquiryType}
                                            onChange={set("enquiryType")}
                                            className="mt-2 w-full appearance-none border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 hover:border-white/40 focus:border-accent"
                                        >
                                            <option
                                                value=""
                                                className="bg-[#0C1428]"
                                            >
                                                Select a topic
                                            </option>
                                            {ENQUIRY_OPTIONS.map((o) => (
                                                <option
                                                    key={o}
                                                    value={o}
                                                    className="bg-[#0C1428]"
                                                >
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-4 w-4 -translate-y-1/3 text-cad" />
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <label
                                        htmlFor="contact-subject"
                                        className="font-mono text-[13px] tracking-[0.22em] text-cad"
                                    >
                                        SUBJECT / REASON FOR CONTACT
                                    </label>
                                    <input
                                        id="contact-subject"
                                        type="text"
                                        required
                                        data-testid="contact-input-subject"
                                        value={form.subject}
                                        onChange={set("subject")}
                                        className="mt-2 w-full border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 hover:border-white/40 focus:border-accent"
                                        placeholder="—"
                                    />
                                </div>

                                <div className="mt-8">
                                    <label
                                        htmlFor="contact-message"
                                        className="font-mono text-[13px] tracking-[0.22em] text-cad"
                                    >
                                        MESSAGE
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        required
                                        rows={4}
                                        maxLength={4000}
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={set("message")}
                                        className="mt-2 w-full resize-none border-b border-white/25 bg-transparent py-3 text-[16px] text-white outline-none transition-colors duration-300 placeholder:text-cad/60 hover:border-white/40 focus:border-accent"
                                        placeholder="—"
                                    />
                                </div>

                                {status === "error" && (
                                    <p
                                        data-testid="contact-error"
                                        className="mt-6 border border-red-500/40 bg-red-500/10 px-4 py-3 font-mono text-[13px] tracking-[0.18em] text-red-300"
                                    >
                                        SOMETHING WENT WRONG — PLEASE TRY
                                        AGAIN.
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={status === "submitting"}
                                    className="mt-10 flex w-full items-center justify-center gap-3 bg-accent px-8 py-5 text-[15px] font-semibold tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#ff7026] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {status === "submitting"
                                        ? "SENDING…"
                                        : "SEND MESSAGE"}
                                    {status !== "submitting" && (
                                        <ArrowUpRight className="h-4 w-4" />
                                    )}
                                </button>
                                <p
                                    data-testid="contact-privacy-notice"
                                    className="mt-5 font-mono text-[13px] leading-relaxed tracking-[0.14em] text-cad"
                                >
                                    YOUR INFORMATION IS USED TO RESPOND TO YOUR
                                    ENQUIRY AND IS HANDLED WITH
                                    POPIA-CONSCIOUS CARE — SEE THE{" "}
                                    <Link
                                        to="/privacy"
                                        data-testid="contact-privacy-link"
                                        className="text-mist underline decoration-white/30 underline-offset-4 transition-colors duration-300 hover:text-accent"
                                    >
                                        PRIVACY POLICY
                                    </Link>
                                    .
                                </p>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
